import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEditor, EditorContent, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { X, Send, Bold, Italic, List, ListOrdered } from 'lucide-react';
import type { PeerData } from '../types';

interface MessageComposerProps {
  isOpen: boolean;
  peer: PeerData | null;
  onClose: () => void;
  onSend: (htmlContent: string) => void;
}

export function MessageComposer({
  isOpen,
  peer,
  onClose,
  onSend,
}: MessageComposerProps) {
  const [isSending, setIsSending] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[200px] px-4 py-3',
      },
    },
  });

  const { isEmpty } = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isEmpty: editor.isEmpty,
    }),
  });

  const handleSend = async () => {
    if (!editor || editor.isEmpty) return;

    setIsSending(true);
    const html = editor.getHTML();
    await onSend(html);
    editor.commands.clearContent();
    setIsSending(false);
    onClose();
  };

  const handleClose = () => {
    editor?.commands.clearContent();
    onClose();
  };

  if (!peer) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', duration: 0.3 }}
              className="w-full max-w-2xl bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <div>
                  <h2 className="text-xl font-bold text-white">Send Message</h2>
                  <p className="text-sm text-gray-400 mt-1">
                    To {peer.name}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800 rounded-lg"
                  aria-label="Close composer"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Toolbar */}
              {editor && (
                <div className="flex items-center gap-1 px-4 py-2 border-b border-gray-700 bg-gray-800/50">
                  <button
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={`p-2 rounded transition-colors ${editor.isActive('bold')
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                      }`}
                    title="Bold"
                  >
                    <Bold size={18} />
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`p-2 rounded transition-colors ${editor.isActive('italic')
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                      }`}
                    title="Italic"
                  >
                    <Italic size={18} />
                  </button>
                  <div className="w-px h-6 bg-gray-700 mx-1" />
                  <button
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={`p-2 rounded transition-colors ${editor.isActive('bulletList')
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                      }`}
                    title="Bullet List"
                  >
                    <List size={18} />
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={`p-2 rounded transition-colors ${editor.isActive('orderedList')
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                      }`}
                    title="Numbered List"
                  >
                    <ListOrdered size={18} />
                  </button>
                </div>
              )}

              {/* Editor */}
              <div className="bg-gray-800/30 text-white min-h-[200px] max-h-[400px] overflow-y-auto">
                <EditorContent editor={editor} />
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-800/50 border-t border-gray-700 flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={isSending || !editor || isEmpty}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all"
                >
                  <Send size={18} />
                  {isSending ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
